/**
 * useInvoiceFilters Hook
 * Manages invoice filter state
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

import { useState, useMemo } from 'react';
import type { InvoiceFilters, UseInvoiceFiltersReturn } from '../types/invoice.types';
import type { InvoiceStatus } from '@/types/invoice';

/**
 * Hook for managing invoice filters
 * Provides state management for search, status, date range, and sorting
 *
 * @example
 * ```tsx
 * const filters = useInvoiceFilters();
 *
 * // Update filters
 * filters.setSearchTerm('INV-001');
 * filters.toggleStatus('PAID');
 * filters.setDateRange('2024-01-01', '2024-12-31');
 *
 * // Clear all filters
 * filters.clearFilters();
 *
 * // Check filter state
 * console.log(filters.hasActiveFilters);
 * console.log(filters.activeFilterCount);
 * ```
 */
export function useInvoiceFilters(): UseInvoiceFiltersReturn {
  // Filter state
  const [searchTerm, setSearchTermState] = useState<string>('');
  const [statuses, setStatusesState] = useState<InvoiceStatus[]>([]);
  const [dateFrom, setDateFromState] = useState<string>('');
  const [dateTo, setDateToState] = useState<string>('');
  const [sortBy, setSortByState] = useState<'invoiceDate' | 'dueDate' | 'totalAmount' | 'status'>('invoiceDate');
  const [sortDirection, setSortDirectionState] = useState<'asc' | 'desc'>('desc');

  // ============================================
  // COMPUTED VALUES
  // ============================================

  const hasActiveFilters = useMemo(() => {
    return Boolean(
      searchTerm ||
      statuses.length > 0 ||
      dateFrom ||
      dateTo
    );
  }, [searchTerm, statuses, dateFrom, dateTo]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchTerm) count++;
    if (statuses.length > 0) count++;
    if (dateFrom) count++;
    if (dateTo) count++;
    return count;
  }, [searchTerm, statuses, dateFrom, dateTo]);

  // ============================================
  // SETTERS
  // ============================================

  const setSearchTerm = (term: string) => {
    setSearchTermState(term);
  };

  const toggleStatus = (status: InvoiceStatus) => {
    setStatusesState((prev) => {
      const alreadySelected = prev.includes(status);
      if (alreadySelected) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  const setStatuses = (newStatuses: InvoiceStatus[]) => {
    setStatusesState(newStatuses);
  };

  const setDateRange = (from: string, to: string) => {
    setDateFromState(from);
    setDateToState(to);
  };

  const setSortBy = (by: 'invoiceDate' | 'dueDate' | 'totalAmount' | 'status') => {
    setSortByState(by);
  };

  const setSortDirection = (dir: 'asc' | 'desc') => {
    setSortDirectionState(dir);
  };

  const clearFilters = () => {
    setSearchTermState('');
    setStatusesState([]);
    setDateFromState('');
    setDateToState('');
    setSortByState('invoiceDate');
    setSortDirectionState('desc');
  };

  // ============================================
  // RETURN
  // ============================================

  return {
    searchTerm,
    setSearchTerm,
    statuses,
    toggleStatus,
    setStatuses,
    dateFrom,
    dateTo,
    setDateRange,
    sortBy,
    setSortBy,
    sortDirection,
    setSortDirection,
    clearFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}

export default useInvoiceFilters;
